/**
 * Shared TCS / GST compliance report — used by `/reports/tcs` and `/reports/gst`.
 *
 * Trip-centric tracking (Task 2): collected tax is aggregated per trip (and per
 * direct, trip-less invoice) from the INVOICES, grouped by filing period
 * (QUARTER for TCS / Form 27EQ, MONTH for GST / GSTR-3B). Each entry tracks the
 * government deposit (paid? date / mode / bank-charges / challan / remarks); each
 * period tracks whether the return was filed. Marking a deposit paid or a return
 * filed syncs the Tax Calendar automatically.
 *
 * Babel note: every `.map()` runs over a LOCAL array (groups / entries / MODES),
 * never a member-expression, to dodge the build-time recursion crash.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Download, FileText, CheckCircle2, Landmark, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEPOSIT_MODES = ['NEFT', 'RTGS', 'Challan', 'Cash', 'Other'];

const fmtINR = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const safeId = (s) => String(s || '').replace(/[^a-zA-Z0-9]/g, '-');
const todayISO = () => new Date().toISOString().slice(0, 10);

const currentFyStart = () => {
  const d = new Date();
  return d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
};
const fyLabel = (start) => `FY ${start}-${String(start + 1).slice(-2)}`;
const buildFyOptions = () => {
  const cur = currentFyStart();
  return [cur + 1, cur, cur - 1, cur - 2].map((s) => ({ value: s, label: fyLabel(s) }));
};

const EMPTY_TOTALS = { collected: 0, paid: 0, pending: 0, bankCharges: 0, count: 0 };

const TaxReportPage = ({ taxKey, title, subtitle }) => {
  const isTcs = taxKey === 'tcs';
  const taxLabel = taxKey.toUpperCase();
  const periodWord = isTcs ? 'Quarter' : 'Period';

  const [fyStart, setFyStart] = useState(currentFyStart());
  const [data, setData] = useState({ groups: [], grandTotals: EMPTY_TOTALS, financialYear: fyLabel(currentFyStart()) });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all | paid | pending
  const [periodFilter, setPeriodFilter] = useState('all');
  const [markTarget, setMarkTarget] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tax/report?tax=${taxKey}&fy=${fyStart}`, { withCredentials: true });
      setData(res.data || { groups: [], grandTotals: EMPTY_TOTALS });
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to load ${taxLabel} report`);
    } finally {
      setLoading(false);
    }
  }, [taxKey, fyStart, taxLabel]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // ---- Deposit (mark paid / unmark) ----
  const submitDeposit = async (entry, form) => {
    try {
      await axios.post(`${BACKEND_URL}/api/tax/deposit`, {
        entityType: entry.entityType,
        entityId: entry.entityId,
        tax: taxKey,
        paidToGovt: true,
        paymentDate: form.paymentDate,
        mode: form.mode,
        bankCharges: Number(form.bankCharges) || 0,
        challanNumber: form.challanNumber,
        remarks: form.remarks,
      }, { withCredentials: true });
      toast.success(`${taxLabel} deposit recorded${entry.calendarLabel ? ' · Tax Calendar updated' : ''}`);
      setMarkTarget(null);
      fetchReport();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record deposit');
    }
  };
  const unmarkDeposit = async (entry) => {
    if (!window.confirm('Mark this entry as NOT yet deposited?')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/tax/deposit`, {
        entityType: entry.entityType, entityId: entry.entityId, tax: taxKey,
        paidToGovt: false, paymentDate: '', mode: '', bankCharges: 0, challanNumber: '', remarks: '',
      }, { withCredentials: true });
      toast.success('Reverted to pending');
      fetchReport();
    } catch (err) {
      toast.error('Failed to revert');
    }
  };

  // ---- Return filing toggle ----
  const toggleReturn = async (group) => {
    const filing = !group.returnFiled;
    const verb = filing ? 'file' : 'reopen';
    if (!window.confirm(`Mark the ${group.periodLabel} ${taxLabel} return as ${filing ? 'FILED' : 'PENDING'}?`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/tax/return/file`, {
        tax: taxKey,
        period: group.period,
        financialYear: group.financialYear,
        calendarLabel: group.calendarLabel,
        status: filing ? 'filed' : 'pending',
      }, { withCredentials: true });
      toast.success(`Return ${filing ? 'marked filed' : 'reopened'}`);
      fetchReport();
    } catch (err) {
      toast.error(`Failed to ${verb} return`);
    }
  };

  // ---- Client-side filters ----
  const periodOptions = useMemo(
    () => (data.groups || []).map((g) => ({ value: g.period, label: g.periodLabel })),
    [data.groups],
  );

  const visibleGroups = useMemo(() => {
    const out = [];
    for (const g of (data.groups || [])) {
      if (periodFilter !== 'all' && g.period !== periodFilter) continue;
      let entries = g.entries || [];
      if (statusFilter === 'paid') entries = entries.filter((e) => e.paidToGovt);
      else if (statusFilter === 'pending') entries = entries.filter((e) => !e.paidToGovt);
      if (entries.length === 0) continue;
      const collected = entries.reduce((s, e) => s + Number(e.taxCollected || 0), 0);
      const paid = entries.filter((e) => e.paidToGovt).reduce((s, e) => s + Number(e.taxCollected || 0), 0);
      const charges = entries.filter((e) => e.paidToGovt).reduce((s, e) => s + Number(e.bankCharges || 0), 0);
      out.push({ ...g, entries, viewTotals: { collected, paid, pending: collected - paid, bankCharges: charges, count: entries.length } });
    }
    return out;
  }, [data.groups, statusFilter, periodFilter]);

  const grand = data.grandTotals || EMPTY_TOTALS;

  // ---- CSV export (Form 27EQ-friendly, grouped) ----
  const downloadCSV = () => {
    const header = [
      periodWord, 'Trip / Invoice ID', 'Client', 'PAN', 'Passport', 'Collection Date',
      `${taxLabel} Collected`, 'Paid to Govt', 'Payment Date', 'Mode',
      'Bank Charges', 'Challan No', 'Remarks', 'Return Filed',
    ];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [header.join(',')];
    for (const g of (data.groups || [])) {
      for (const e of (g.entries || [])) {
        lines.push([
          esc(g.periodLabel), esc(e.displayId), esc(e.clientName), esc(e.pan), esc(e.passport),
          esc(e.collectionDate), e.taxCollected, e.paidToGovt ? 'Yes' : 'No',
          esc(e.paymentDate), esc(e.mode), e.bankCharges || 0, esc(e.challanNumber),
          esc(e.remarks), g.returnFiled ? 'Yes' : 'No',
        ].join(','));
      }
      lines.push([
        esc(`${g.periodLabel} — SUBTOTAL`), '', '', '', '', '',
        g.totals.collected, `Paid ${g.totals.paid}`, '', '',
        g.totals.bankCharges, '', `Pending ${g.totals.pending}`, g.returnFiled ? 'Yes' : 'No',
      ].join(','));
    }
    lines.push([esc('GRAND TOTAL'), '', '', '', '', '', grand.collected, `Paid ${grand.paid}`, '', '', grand.bankCharges, '', `Pending ${grand.pending}`, ''].join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${taxKey}-27eq-${data.financialYear || fyLabel(fyStart)}.csv`.replace(/\s/g, '');
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${taxLabel} ${data.financialYear}`);
  };

  const fyOptions = buildFyOptions();
  const statusChips = [
    { k: 'all', label: 'All' },
    { k: 'paid', label: 'Paid to Govt' },
    { k: 'pending', label: 'Pending' },
  ];

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid={`${taxKey}-report-page`}>
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-emerald-600" />
            {title}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <Label className="text-xs">Financial Year</Label>
            <Select value={String(fyStart)} onValueChange={(v) => setFyStart(Number(v))}>
              <SelectTrigger className="w-36 mt-1" data-testid={`${taxKey}-fy-select`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {fyOptions.map((o) => (
                  <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={downloadCSV} data-testid={`${taxKey}-export-csv-btn`} className="gap-2">
            <Download className="h-4 w-4" /> Export CSV (27EQ)
          </Button>
        </div>
      </header>

      {/* Summary strip */}
      <Card className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100" data-testid={`${taxKey}-summary-strip`}>
        <SummaryCell label={`${taxLabel} Collected`} value={fmtINR(grand.collected)} sub={`${grand.count} entries`} testid={`${taxKey}-summary-collected`} />
        <SummaryCell label="Deposited to Govt" value={fmtINR(grand.paid)} tone="emerald" testid={`${taxKey}-summary-paid`} />
        <SummaryCell label="Pending Deposit" value={fmtINR(grand.pending)} tone={grand.pending > 0 ? 'amber' : 'emerald'} testid={`${taxKey}-summary-pending`} />
        <SummaryCell label="Bank Charges" value={fmtINR(grand.bankCharges)} testid={`${taxKey}-summary-charges`} />
      </Card>

      {/* Filters */}
      <Card className="p-3 flex flex-wrap gap-3 items-end" data-testid={`${taxKey}-filters`}>
        <div className="inline-flex bg-slate-100 rounded-lg p-0.5">
          {statusChips.map((c) => (
            <button
              key={c.k}
              type="button"
              onClick={() => setStatusFilter(c.k)}
              data-testid={`${taxKey}-filter-${c.k}`}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${statusFilter === c.k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div>
          <Select value={periodFilter} onValueChange={setPeriodFilter}>
            <SelectTrigger className="w-52" data-testid={`${taxKey}-period-filter`}><SelectValue placeholder={`By ${periodWord}`} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All {periodWord}s</SelectItem>
              {periodOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm" data-testid={`${taxKey}-loading`}>Loading…</div>
      ) : visibleGroups.length === 0 ? (
        <Card className="text-center py-12 text-slate-400 text-sm" data-testid={`${taxKey}-empty`}>
          <Landmark className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          No {taxLabel} collected in {data.financialYear}.
        </Card>
      ) : (
        visibleGroups.map((g) => (
          <PeriodSection
            key={g.period}
            group={g}
            taxKey={taxKey}
            taxLabel={taxLabel}
            onMark={setMarkTarget}
            onUnmark={unmarkDeposit}
            onToggleReturn={toggleReturn}
          />
        ))
      )}

      <MarkPaidDialog
        target={markTarget}
        taxKey={taxKey}
        taxLabel={taxLabel}
        onClose={() => setMarkTarget(null)}
        onSubmit={submitDeposit}
      />
    </div>
  );
};

const SummaryCell = ({ label, value, sub, tone, testid }) => {
  const color = tone === 'emerald' ? 'text-emerald-700' : tone === 'amber' ? 'text-amber-600' : 'text-slate-900';
  return (
    <div className="p-4" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-mono mt-1 font-semibold ${color}`}>{value}</div>
      {sub ? <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div> : null}
    </div>
  );
};

const PeriodSection = ({ group, taxKey, taxLabel, onMark, onUnmark, onToggleReturn }) => {
  const t = group.viewTotals || group.totals;
  const entries = group.entries || [];
  const isTcs = taxKey === 'tcs';
  return (
    <Card className="overflow-hidden" data-testid={`${taxKey}-period-${group.period}`}>
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2 bg-slate-50/60">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-bold text-slate-800">{group.periodLabel}</h3>
          {group.returnFiled ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`${taxKey}-return-badge-${group.period}`}>
              <CheckCircle2 className="h-3 w-3" /> Return Filed{group.returnFiledBy ? ` · ${group.returnFiledBy}` : ''}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid={`${taxKey}-return-badge-${group.period}`}>
              <Clock className="h-3 w-3" /> Return Pending
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-slate-500">Collected <span className="font-mono font-semibold text-slate-800">{fmtINR(t.collected)}</span></span>
          <span className="text-xs text-slate-500">Deposited <span className="font-mono font-semibold text-emerald-700">{fmtINR(t.paid)}</span></span>
          <span className="text-xs text-slate-500">Pending <span className="font-mono font-semibold text-amber-600">{fmtINR(t.pending)}</span></span>
          <Button
            size="sm"
            variant={group.returnFiled ? 'outline' : 'default'}
            className={group.returnFiled ? '' : 'bg-emerald-700 hover:bg-emerald-800 text-white'}
            onClick={() => onToggleReturn(group)}
            data-testid={`${taxKey}-return-toggle-${group.period}`}
          >
            {group.returnFiled ? 'Reopen Return' : 'Mark Return Filed'}
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid={`${taxKey}-period-table-${group.period}`}>
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            <tr>
              <th className="px-3 py-2 text-left">Trip / Invoice</th>
              <th className="px-3 py-2 text-left">Client</th>
              <th className="px-3 py-2 text-left">PAN</th>
              {isTcs ? <th className="px-3 py-2 text-left">Passport</th> : null}
              <th className="px-3 py-2 text-right">{taxLabel} Collected</th>
              <th className="px-3 py-2 text-center">Deposited</th>
              <th className="px-3 py-2 text-left">Payment / Mode</th>
              <th className="px-3 py-2 text-right">Bank Chg.</th>
              <th className="px-3 py-2 text-left">Challan</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {entries.map((e) => (
              <EntryRow
                key={`${e.entityType}-${e.entityId}`}
                entry={e}
                taxKey={taxKey}
                isTcs={isTcs}
                onMark={onMark}
                onUnmark={onUnmark}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const EntryRow = ({ entry, taxKey, isTcs, onMark, onUnmark }) => {
  const e = entry;
  const idSafe = safeId(e.entityId);
  return (
    <tr className="hover:bg-slate-50" data-testid={`${taxKey}-entry-${idSafe}`}>
      <td className="px-3 py-2 font-mono text-[11px] text-slate-700">
        {e.displayId}
        {e.entityType === 'invoice' ? <span className="ml-1 text-[9px] text-slate-400">(direct)</span> : null}
      </td>
      <td className="px-3 py-2 text-slate-700">{e.clientName || '—'}</td>
      <td className={`px-3 py-2 font-mono ${!e.pan ? 'text-rose-500' : 'text-slate-600'}`}>{e.pan || 'NO PAN'}</td>
      {isTcs ? (
        <td className={`px-3 py-2 font-mono ${!e.passport ? 'text-slate-400' : 'text-slate-600'}`}>{e.passport || '—'}</td>
      ) : null}
      <td className="px-3 py-2 text-right font-mono font-semibold text-slate-800">{fmtNum(e.taxCollected)}</td>
      <td className="px-3 py-2 text-center">
        {e.paidToGovt ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Yes</span>
        ) : (
          <span className="text-[11px] font-semibold text-amber-600">No</span>
        )}
      </td>
      <td className="px-3 py-2 text-slate-600">
        {e.paidToGovt ? (
          <span>{e.paymentDate || '—'}{e.mode ? ` · ${e.mode}` : ''}</span>
        ) : '—'}
      </td>
      <td className="px-3 py-2 text-right font-mono text-slate-600">{e.paidToGovt && e.bankCharges ? fmtNum(e.bankCharges) : '—'}</td>
      <td className="px-3 py-2 font-mono text-slate-600">{e.challanNumber || '—'}</td>
      <td className="px-3 py-2 text-right">
        {e.paidToGovt ? (
          <div className="inline-flex gap-1">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onMark(e)} data-testid={`${taxKey}-edit-${idSafe}`}>Edit</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-500" onClick={() => onUnmark(e)} data-testid={`${taxKey}-unmark-${idSafe}`}>Undo</Button>
          </div>
        ) : (
          <Button size="sm" className="h-7 text-xs bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => onMark(e)} data-testid={`${taxKey}-markpaid-${idSafe}`}>
            Mark Paid
          </Button>
        )}
      </td>
    </tr>
  );
};

const MarkPaidDialog = ({ target, taxKey, taxLabel, onClose, onSubmit }) => {
  const [form, setForm] = useState({ paymentDate: todayISO(), mode: 'NEFT', bankCharges: '0', challanNumber: '', remarks: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) {
      setForm({
        paymentDate: target.paymentDate || todayISO(),
        mode: target.mode || 'NEFT',
        bankCharges: String(target.bankCharges ?? '0'),
        challanNumber: target.challanNumber || '',
        remarks: target.remarks || '',
      });
    }
  }, [target]);

  const submit = async () => {
    setSaving(true);
    await onSubmit(target, form);
    setSaving(false);
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid={`${taxKey}-markpaid-dialog`}>
        <DialogHeader>
          <DialogTitle>Confirm {taxLabel} deposit to government</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Records the deposit (date, mode, bank charges, challan) for this entry, logs who marked it, and updates the Tax Calendar.
          </DialogDescription>
        </DialogHeader>
        {target ? (
          <div className="space-y-3 mt-1">
            <div className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <span className="font-mono font-semibold text-slate-800">{target.displayId}</span>
              <span className="text-slate-500">{taxLabel} collected <span className="font-semibold text-slate-800">{fmtINR(target.taxCollected)}</span></span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Payment date</Label>
                <Input type="date" value={form.paymentDate} onChange={(e) => setForm((f) => ({ ...f, paymentDate: e.target.value }))} className="mt-1" data-testid={`${taxKey}-md-date`} />
              </div>
              <div>
                <Label className="text-xs">Mode</Label>
                <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
                  <SelectTrigger className="mt-1" data-testid={`${taxKey}-md-mode`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DEPOSIT_MODES.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Bank charges ₹</Label>
                <Input type="number" min="0" value={form.bankCharges} onChange={(e) => setForm((f) => ({ ...f, bankCharges: e.target.value }))} className="mt-1" data-testid={`${taxKey}-md-charges`} />
              </div>
              <div>
                <Label className="text-xs">Challan number</Label>
                <Input value={form.challanNumber} onChange={(e) => setForm((f) => ({ ...f, challanNumber: e.target.value }))} className="mt-1" data-testid={`${taxKey}-md-challan`} placeholder="e.g. CIN / BSR ref" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Remarks</Label>
              <Input value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} className="mt-1" data-testid={`${taxKey}-md-remarks`} placeholder="Optional note" />
            </div>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={`${taxKey}-md-cancel`}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`${taxKey}-md-confirm`}>
            {saving ? 'Saving…' : 'Confirm deposit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaxReportPage;
